import React, {useCallback, useEffect} from 'react'
import { useTranslation } from 'react-i18next'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {Button, Form, Space, Typography} from 'antd'

import {getRouterStore, withTitle} from '@/router/utils'
import {Alert, Card, Container, PageContainer, Select} from '@/components'

import {BankAccountCard} from '@/pages/BankAccounts/components'
import {
    AssociatedOutlets,
    BankAccountSelect,
    BankChangeModals
} from '@/pages/ChangeBankAccountDetails/components'
import {ChangeBankAccountDetailsRequest} from '@/stores/change-bank-account-details/models'
import {GoogleManagerTagClassNames} from '@/constants'
import {hasPermissions} from '@/stores/auth/services'
import {PermissionsMap} from '@/stores/auth/constants/permissions-map'
import {ROUTES} from '@/router/routes'
import {
    ACCOUNT_OPTION,
    CANCEL_CHANGE,
    CONFIRM_BUTTON,
    PAYMENT_OPTIONS,
    REASON
} from '@/constants/playwright-ids'

import {IChangeBankAccountDetails} from './IChangeBankAccountDetails'
import styles from './ChangeBankAccountDetails.scss'
import {LoadingState} from '@/types'

export const ChangeBankAccountDetailsStoreSymbol = Symbol.for('ChangeBankAccountDetailsStoreSymbol')

const ChangeBankAccountDetails = observer(() => {
    const { t } = useTranslation()
    const {
        currentBankAccount,
        bankAccounts,
        selectedBankAccount,
        accountType,
        selectBankAccount,
        isLoading,
        onBack,
        openConfirmBankDetailsModal,
        changeBankDetails,
        onCloseConfirmModal,
        modalState,
        onCloseSuccessModal,
        onCloseErrorModal,
        bankAccountsLoading,
        hasSettlementBlocks,
        hasDirectDebitBlocks,
        hasAllBlocks,
        selectAccountType,
        setSelectAccountType,
        shouldChangeAllOutletsBankDetails,
        outletDetails,
        currentFullBankAccountRelatedStores,
        bankDetailReasons,
        reasonsLoadingState,
        loadBankDetailReasons
    } = useInjection<IChangeBankAccountDetails>(ChangeBankAccountDetailsStoreSymbol)
    const [form] = Form.useForm()

    useEffect(() => {
        loadBankDetailReasons()
    }, [])

    if (
        window.location.pathname.includes('online-outlets') &&
        !hasPermissions([PermissionsMap.merchant.ecom_stores.full])
    ) {
        getRouterStore().push(ROUTES.noPermissions)
    }

    if (
        window.location.pathname.includes('pos-outlets') &&
        !hasPermissions([PermissionsMap.merchant.pos_stores.full])
    ) {
        getRouterStore().push(ROUTES.noPermissions)
    }

    const handleSubmit = (
        values: Pick<ChangeBankAccountDetailsRequest, 'paymentMethod' | 'changeBankDetailReason'>
    ) => {
        openConfirmBankDetailsModal({
            paymentMethod: values.paymentMethod,
            changeBankDetailReason: values.changeBankDetailReason
        })
    }

    const isSettlementAccount = accountType === 'settlement'
    const disabled = bankAccounts?.length === 0

    const confirmButtonTag =
        accountType === 'all'
            ? GoogleManagerTagClassNames.settings.outlets.changeBankDetails.all.confirmChange
            : accountType === 'settlement'
              ? GoogleManagerTagClassNames.settings.outlets.changeBankDetails.settlements
                    .confirmChange
              : GoogleManagerTagClassNames.settings.outlets.changeBankDetails.directDebit
                    .confirmChange

    const cancelButtonTag =
        accountType === 'all'
            ? GoogleManagerTagClassNames.settings.outlets.changeBankDetails.all.cancel
            : accountType === 'settlement'
              ? GoogleManagerTagClassNames.settings.outlets.changeBankDetails.settlements.cancel
              : GoogleManagerTagClassNames.settings.outlets.changeBankDetails.directDebit.cancel

    const renderAlert = useCallback(() => {
        let description

        if (selectAccountType === 'all') {
            description = (<>
                {t('ChangeBankAccountDetails.labels.allAccountChangeAlert.text')}
                <ul>
                    <li>{t('ChangeBankAccountDetails.labels.allAccountChangeAlert.list.item1')}</li>
                    <li>{t('ChangeBankAccountDetails.labels.allAccountChangeAlert.list.item2')}</li>
                </ul>
            </>)
        } else if (!selectAccountType) {
            description = isSettlementAccount
                ? t('ChangeBankAccountDetails.labels.settlementAccountChangeAlert')
                : t('ChangeBankAccountDetails.labels.directDebitAccountChangeAlert')
        } else {
            description =
                selectAccountType === 'settlement'
                    ? t('ChangeBankAccountDetails.labels.settlementAccountChangeAlert')
                    : t('ChangeBankAccountDetails.labels.directDebitAccountChangeAlert')
        }

        return <Alert description={description} />
    }, [selectAccountType, isSettlementAccount])

    const shouldShowSeparateOutlets = React.useMemo(() => {
        const changeModel = shouldChangeAllOutletsBankDetails
        return selectAccountType === 'all' && changeModel.directDebit !== changeModel.settlement
    }, [selectAccountType, shouldChangeAllOutletsBankDetails])

    const currentOutletStoreName = outletDetails?.storeName
    const currentOutletArray = React.useMemo(
        () => (currentOutletStoreName ? [currentOutletStoreName] : []),
        [currentOutletStoreName]
    )

    const renderAssociatedOutlets = () => {
        if (shouldShowSeparateOutlets) {
            return (
                <>
                    <AssociatedOutlets
                        title={t('ChangeBankAccountDetails.labels.associatedDirectDebitOutlets')}
                        outlets={directDebitOutlets}
                    />
                    <AssociatedOutlets
                        title={t('ChangeBankAccountDetails.labels.associatedSettlementOutlets')}
                        outlets={settlementOutlets}
                    />
                </>
            )
        }

        if (accountType === 'all') {
            return <AssociatedOutlets outlets={directDebitOutlets} />
        }

        if (accountType === 'directDebit') {
            return <AssociatedOutlets outlets={directDebitOutlets} />
        }

        if (accountType === 'settlement') {
            return <AssociatedOutlets outlets={settlementOutlets} />
        }

        return <AssociatedOutlets outlets={currentOutletArray} />
    }

    const directDebitOutlets = React.useMemo(
        () =>
            shouldChangeAllOutletsBankDetails.directDebit
                ? currentFullBankAccountRelatedStores
                : currentOutletArray,
        [
            shouldChangeAllOutletsBankDetails.directDebit,
            currentFullBankAccountRelatedStores,
            currentOutletArray
        ]
    )

    const settlementOutlets = React.useMemo(
        () =>
            shouldChangeAllOutletsBankDetails.settlement
                ? currentFullBankAccountRelatedStores
                : currentOutletArray,
        [
            shouldChangeAllOutletsBankDetails.settlement,
            currentFullBankAccountRelatedStores,
            currentOutletArray
        ]
    )

    return (
        <Container className={styles.container}>
            <PageContainer
                title={
                    isSettlementAccount
                        ? t('ChangeBankAccountDetails.titles.changeSettlementAccount')
                        : t('ChangeBankAccountDetails.titles.changeDirectDebitAccount')
                }
                isLoading={false}
                onBack={onBack}
            >
                {currentBankAccount ? (
                    <BankAccountCard
                        className={styles.selectedBankAccount}
                        key={currentBankAccount.id}
                        bank={currentBankAccount.bankName}
                        logo={currentBankAccount.logo}
                        sortCode={currentBankAccount.sortCode}
                        accountNumber={currentBankAccount.bankAccountNumber}
                    />
                ) : (
                    <Card loading={true} />
                )}

                <Typography.Title level={4}>{t('ChangeBankAccountDetails.titles.changeTo')}</Typography.Title>

                <Form form={form} onFinish={handleSubmit} layout='vertical'>
                    <Form.Item
                        name='bankAccount'
                        rules={[{required: true, message: t('ChangeBankAccountDetails.labels.pleaseSelectBankAccount')}]}
                    >
                        <BankAccountSelect
                            value={selectedBankAccount}
                            accounts={bankAccounts}
                            onChange={selectBankAccount}
                            loading={bankAccountsLoading}
                        />
                    </Form.Item>

                    <Form.Item
                        name='paymentMethod'
                        label={
                            isSettlementAccount
                                ? t('ChangeBankAccountDetails.labels.applyDirectDebitPayments')
                                : t('ChangeBankAccountDetails.labels.applySettlementPayments')
                        }
                        rules={[
                            {
                                required: true,
                                message: t('ChangeBankAccountDetails.labels.pleaseSelectBankAccount')
                            }
                        ]}
                        data-test-id={PAYMENT_OPTIONS}
                    >
                        <Select
                            placeholder={t('ChangeBankAccountDetails.labels.selectOption')}
                            disabled={disabled}
                            options={[
                                {
                                    label: t('ChangeBankAccountDetails.labels.useBothPayments'),
                                    value: 'all',
                                    disabled:
                                        (accountType === 'directDebit' && hasSettlementBlocks) ||
                                        (accountType === 'settlement' && hasDirectDebitBlocks) ||
                                        hasAllBlocks
                                },
                                {
                                    label: t('ChangeBankAccountDetails.labels.useAccountTypeOnly').replace(
                                        '{accountType}',
                                        accountType === 'directDebit'
                                            ? t('ChangeBankAccountDetails.labels.directDebit')
                                            : accountType
                                    ),
                                    value: accountType
                                }
                            ]}
                            onChange={setSelectAccountType}
                            data-test-id={ACCOUNT_OPTION}
                        />
                    </Form.Item>

                    <Form.Item
                        name='changeBankDetailReason'
                        label={t('ChangeBankAccountDetails.labels.reasonForChange')}
                        rules={[
                            {
                                required: true,
                                message: t('ChangeBankAccountDetails.labels.pleaseSelectReasonForChange')
                            }
                        ]}
                    >
                        <Select
                            placeholder={t('ChangeBankAccountDetails.labels.selectReasonForChange')}
                            disabled={disabled}
                            loading={reasonsLoadingState === LoadingState.LOADING}
                            options={bankDetailReasons.map((reason) => ({
                                label: reason.value,
                                value: reason.key
                            }))}
                            data-test-id={REASON}
                        />
                    </Form.Item>

                    {selectedBankAccount && selectAccountType && (
                        <>
                            {renderAlert()}
                            {renderAssociatedOutlets()}
                        </>
                    )}

                    <br />
                    <Form.Item>
                        <Space>
                            <Button
                                disabled={disabled}
                                type='primary'
                                htmlType='submit'
                                className={confirmButtonTag}
                                data-test-id={CONFIRM_BUTTON}
                            >
                                <span className={confirmButtonTag}>{t('ChangeBankAccountDetails.buttons.confirmChange')}</span>
                            </Button>
                            <Button
                                className={cancelButtonTag}
                                onClick={onBack}
                                data-test-id={CANCEL_CHANGE}
                            >
                                <span className={cancelButtonTag}>{t('ChangeBankAccountDetails.buttons.cancel')}</span>
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>

                <BankChangeModals
                    currentAccount={currentBankAccount}
                    selectedAccount={selectedBankAccount}
                    accountType={selectAccountType}
                    confirmModal={{
                        isOpen: modalState.confirm,
                        isLoading: isLoading,
                        onConfirm: changeBankDetails,
                        onClose: onCloseConfirmModal
                    }}
                    successModal={{
                        isOpen: modalState.success,
                        onClose: onCloseSuccessModal
                    }}
                    errorModal={{
                        isOpen: modalState.error,
                        onClose: onCloseErrorModal
                    }}
                />
            </PageContainer>
        </Container>
    )
})

export default withTitle(ChangeBankAccountDetails)
